import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CalendarCheck, CheckCircle2, Clock, Mail, ShieldCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { PRIMARY_BTN, SiteFooter, SiteHeader } from '../components/home/SiteChrome';
import { ApiService } from '../utils/api';

export const EVENT_TYPES = ['Conference', 'Medical / CME', 'Corporate event', 'Exhibition / Expo', 'College / Academic',
  'Concert / Festival', 'Sports', 'Other'];
export const SIZES = ['Under 200', '200 - 1,000', '1,000 - 5,000', '5,000 - 20,000', '20,000+'];

const EMPTY = {
  name: '', email: '', phone: '', organisation: '', role: '', eventType: '', attendees: '', eventDate: '', city: '', message: '', website: '',
};

const SELECT_CLASS = 'flex h-10 w-full rounded-md border border-input bg-white px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';

const Field = ({ id, label, required, children }) => (
  <div>
    <Label htmlFor={id}>{label}{required && <span className="text-red-600"> *</span>}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

const BookDemoPage = () => {
  const [params] = useSearchParams();
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  useEffect(() => {
    document.title = 'Book a demo | EventEase';
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setError('');
    try {
      await ApiService.submitDemoEnquiry({ ...form, source: params.get('from') || 'demo' });
      setStatus('done');
      window.scrollTo(0, 0);
    } catch (err) {
      setError(err.message);
      setStatus('idle');
    }
  };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <SiteHeader />
      <main className="relative overflow-hidden bg-[#160a2b]">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(249,115,22,0.22),transparent_55%),radial-gradient(ellipse_at_bottom_left,rgba(139,92,246,0.35),transparent_55%)]" />
        <div className="relative mx-auto grid max-w-6xl gap-10 px-4 py-12 sm:px-6 sm:py-16 lg:grid-cols-5 lg:px-8">
          <div className="text-white lg:col-span-2">
            <p className="text-sm font-semibold uppercase tracking-wide text-orange-300">Book a demo</p>
            <h1 className="mt-2 text-3xl font-bold leading-tight sm:text-4xl">See EventEase running on your event</h1>
            <p className="mt-4 text-slate-300">
              Tell us a little about your event. We'll set up a walkthrough with your own registration form, pricing and badge, and answer anything about payments, check-in or on-site operations.
            </p>
            <ul className="mt-8 space-y-4 text-sm text-slate-200">
              <li className="flex gap-3"><Clock className="h-5 w-5 shrink-0 text-orange-300" />We reply within one business day.</li>
              <li className="flex gap-3"><CalendarCheck className="h-5 w-5 shrink-0 text-orange-300" />A 30-minute call, scheduled around you.</li>
              <li className="flex gap-3"><ShieldCheck className="h-5 w-5 shrink-0 text-orange-300" />Your details are used only to respond to this request.</li>
              <li className="flex gap-3"><Mail className="h-5 w-5 shrink-0 text-orange-300" />
                <span>Prefer email? <a href="mailto:info@nextwavesaas.tech" className="underline hover:text-white">info@nextwavesaas.tech</a></span>
              </li>
            </ul>
          </div>

          <div className="rounded-2xl bg-white p-6 shadow-2xl sm:p-8 lg:col-span-3">
            {status === 'done' ? (
              <div className="py-10 text-center" role="status">
                <CheckCircle2 className="mx-auto h-12 w-12 text-emerald-500" />
                <h2 className="mt-4 text-2xl font-bold">Thanks, {form.name.split(' ')[0]}!</h2>
                <p className="mx-auto mt-2 max-w-md text-gray-600">
                  We've received your request and sent a confirmation to <b>{form.email}</b>. Our team will be in touch within one business day.
                </p>
                <Button asChild variant="outline" className="mt-6"><Link to="/">Back to home</Link></Button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field id="d-name" label="Full name" required>
                    <Input id="d-name" required maxLength={100} autoComplete="name" value={form.name} onChange={set('name')} />
                  </Field>
                  <Field id="d-email" label="Work email" required>
                    <Input id="d-email" type="email" required maxLength={254} autoComplete="email" value={form.email} onChange={set('email')} />
                  </Field>
                  <Field id="d-phone" label="Phone" required>
                    <Input id="d-phone" type="tel" required maxLength={30} autoComplete="tel" placeholder="+91 98xxx xxxxx"
                      value={form.phone} onChange={set('phone')} />
                  </Field>
                  <Field id="d-org" label="Organisation" required>
                    <Input id="d-org" required maxLength={150} autoComplete="organization" value={form.organisation} onChange={set('organisation')} />
                  </Field>
                  <Field id="d-role" label="Your role">
                    <Input id="d-role" maxLength={100} autoComplete="organization-title" placeholder="e.g. Organising secretary" value={form.role} onChange={set('role')} />
                  </Field>
                  <Field id="d-type" label="Event type">
                    <select id="d-type" className={SELECT_CLASS} value={form.eventType} onChange={set('eventType')}>
                      <option value="">Select</option>
                      {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                  <Field id="d-size" label="Expected attendees">
                    <select id="d-size" className={SELECT_CLASS} value={form.attendees} onChange={set('attendees')}>
                      <option value="">Select</option>
                      {SIZES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                  <Field id="d-date" label="Event date">
                    <Input id="d-date" maxLength={40} placeholder="e.g. March 2027" value={form.eventDate} onChange={set('eventDate')} />
                  </Field>
                </div>
                <Field id="d-city" label="City">
                  <Input id="d-city" maxLength={80} autoComplete="address-level2" value={form.city} onChange={set('city')} />
                </Field>
                <Field id="d-message" label="What would you like to see?">
                  <Textarea id="d-message" rows={4} maxLength={2000} placeholder="Registration flow, payments, face check-in, badge printing…"
                    value={form.message} onChange={set('message')} />
                </Field>
                <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
                  className="hidden" value={form.website} onChange={set('website')} />
                {error && <p className="text-sm text-red-600" role="alert">{error}</p>}
                <Button type="submit" size="lg" className={`w-full ${PRIMARY_BTN}`} disabled={status === 'sending'}>
                  {status === 'sending' ? 'Sending…' : 'Request a demo'}
                </Button>
                <p className="text-center text-xs text-gray-500">
                  By submitting, you agree to our <Link to="/privacy-policy" className="underline">privacy policy</Link>.
                </p>
              </form>
            )}
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
};

export default BookDemoPage;
